import { Button, Modal, ModalClose } from "@govlaunch/core";
import * as palette from "@govlaunch/palette";
import ProfilePicture from "@govlaunch/profile-picture";
import groupBy from "lodash/fp/groupBy";
import React from "react";
import { Toggle } from "react-powerplug";
import { useToggle } from "react-use";
import { IVendorQuery } from "~/lib/queries/__generated__/VendorQuery.generated";
import GovernmentRichLink from "~/components/government/GovernmentRichLink";
import LinkIcon from "~/components/icons/LinkIcon";
import GovernmentsIcon from "~/components/icons/VendorProfile/GovernmentsIcon";
import mediaQueries from "~/components/mediaQueries";
import { Margin } from "~/components/spacings";
import InviteGovernmentsPlaceholder from "~/components/vendor/InviteGovernmentsPlaceholder";
import InviteGovernmentsModal from "~/components/vendor/profile/InviteGovernmentsModal";
import PendingGovernmentInvites from "~/components/vendor/profile/PendingGovernmentInvites";
import UpgradeAccountBanner from "~/components/vendor/profile/UpgradeAccountBanner";
import { FormattedMessage } from "react-intl";

interface IVendorInviteGovernmentsProps {
  vendor: NonNullable<IVendorQuery["company"]>;
}

export default function VendorInviteGovernments({ vendor }: IVendorInviteGovernmentsProps) {
  const [isOpen, toggle] = useToggle(false);
  const requestsByProduct = groupBy("product._id", vendor.pendingProductStackRequests);

  if (!vendor.plan.limits.inviteGovernments && vendor.viewerIsMember) {
    return <UpgradeAccountBanner />;
  }

  return (
    <>
      <InviteGovernmentsModal vendor={vendor} isOpen={isOpen} onRequestClose={toggle} />

      {vendor.pendingProductStackRequests.length === 0 ? (
        <InviteGovernmentsPlaceholder slug={vendor.slug} onClickToggle={toggle} />
      ) : (
        <div>
          <Margin mb={16}>
            <Button
              theme="secondary"
              size="large"
              type="button"
              onClick={toggle}
              color={palette.lightestGray}
              textColor={palette.darkGray}
              css={{
                background: "#fff",
                fontSize: 16,
                display: "inline-flex",
                alignItems: "center",
              }}
            >
              <GovernmentsIcon width={16} height={16} color={palette.darkGray} />
              <span
                css={{
                  marginLeft: 8,
                  height: 15,
                }}
              >
                <FormattedMessage defaultMessage="Add Government" id="5/r8pz" />
              </span>
            </Button>
          </Margin>

          <div
            css={{
              marginBottom: 16,
              "> div:not(first-of-type)": {
                marginTop: 16,
              },
            }}
          >
            {Object.keys(requestsByProduct).map(productId => {
              const requests = requestsByProduct[productId];

              return (
                <div
                  key={productId}
                  css={{
                    background: "#fff",
                    borderRadius: 4,
                    border: `1px solid ${palette.lightestGray}`,
                  }}
                >
                  <div
                    css={{
                      fontWeight: 500,
                      color: palette.darkGray,
                      fontSize: 16,
                      padding: 16,
                      borderBottom: `1px solid ${palette.lightestGray}`,
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <ProfilePicture image={requests[0].product.logo} name={requests[0].product.name} size={24} />{" "}
                    <span css={{ marginLeft: 8 }}>
                      {requests[0].product.name} <FormattedMessage defaultMessage="Invites" id="Wy+Idb" /> ({requests.length})
                    </span>
                    <Toggle initial={false}>
                      {({ on: isOpen, toggle }) => (
                        <>
                          <span
                            role="button"
                            onClick={toggle}
                            css={{
                              cursor: "pointer",
                              marginLeft: "auto",
                              fontWeight: 500,
                              fontSize: 16,
                              display: "inline-flex",
                              alignItems: "center",
                              color: palette.innovatorBlue,
                            }}
                          >
                            <LinkIcon fill={palette.innovatorBlue} css={{ marginRight: 8 }} />{" "}
                            <span>
                              <FormattedMessage defaultMessage="Invite Link" id="iLUQIP" />
                            </span>
                          </span>
                          <Modal
                            ariaHideApp={false}
                            isOpen={isOpen}
                            onRequestClose={toggle}
                            destroyOnClose={true}
                            overlayStyle={{
                              zIndex: 1000,
                            }}
                            style={{
                              overflow: "visible",
                            }}
                          >
                            <ModalClose onClick={toggle} />
                            <div
                              css={mediaQueries({
                                width: [350, 350, 580, 580],
                                padding: 32,
                              })}
                            >
                              <PendingGovernmentInvites
                                product={requests[0].product}
                                productStackRequests={requests}
                                vendor={vendor}
                                onRequestClose={toggle}
                              />
                            </div>
                          </Modal>
                        </>
                      )}
                    </Toggle>
                  </div>
                  <div
                    css={{
                      padding: 16,
                    }}
                  >
                    <div
                      css={mediaQueries({
                        display: "grid",
                        gridTemplateColumns: ["1fr", "repeat(3, 1fr)"],
                        gridGap: 16,
                      })}
                    >
                      {requests.map(request => (
                        <GovernmentRichLink key={request._id} government={request.government} />
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
}
